import React, { useState, useEffect } from "react";
import { InlineNotification, Loading, Modal } from "carbon-components-react";
import { connect } from "react-redux";
import * as _ from "underscore";

import { AppShape, PowerOfAttorney } from "@eave/shared/types/api";

import { LoadingState } from "underwriter_dashboard/shared/types";
import APIClient from "web_modules/APIClient";
import BaseTable, { EditingState } from "web_modules/shared/BaseTable";

import { loadApp } from "underwriter_dashboard/actions";

import AddPowerOfAttorneyForm from "./AddPowerOfAttorneyForm";
import {
  deletePowerOfAttorney,
  upsertPowerOfAttorney,
} from "underwriter_dashboard/hurdle_editors/Vesting/api";
import { initialValues } from "./FormValues";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

const PowerOfAttorneys = ({
  refreshApp,
  app,
  powerOfAttorneys,
}: {
  refreshApp: any;
  app: AppShape;
  powerOfAttorneys: PowerOfAttorney[];
}) => {
  const [editingMode, updateEditingMode] = useState(EditingState.CREATE);
  const [loadingState, setLoadingState] = useState(LoadingState.INACTIVE);

  const [formOpen, updateFormOpen] = useState(false);
  const [formState, updateFormState] = useState(initialValues(app.id));

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [powerOfAttorneyOptions, setPowerOfAttorneyOptions] = useState({});

  const loadPowerOfAttorneyOptions = () =>
    APIClient.get(`/loan_applications/${app.id}/hurdles/vesting/power_of_attorney_options`).then(
      (resp: any) => {
        setPowerOfAttorneyOptions(resp.options);
      },
    );

  useEffect(() => {
    setLoadingState(LoadingState.ACTIVE);

    loadPowerOfAttorneyOptions();
    setLoadingState(LoadingState.INACTIVE);
  }, [app]);

  return (
    <>
      <BaseTable
        key="power-of-attorneys"
        title="Power Of Attorneys"
        disableBatchEdit
        allowHeaderTextWrapping
        disableBatchDelete
        disableBatch
        rowHeight="short"
        useZebraStyles
        openModal={() => updateFormOpen(true)}
        updateFormState={data => updateFormState(data)}
        updateEditingMode={updateEditingMode}
        headerData={[
          { header: "Borrower / Principal", key: "borrower_principal" },
          { header: "Authorized Agent", key: "authorized_agent" },
          { header: "Other Authorized Agent Name", key: "other_authorized_agent_name" },
        ]}
        rawRowData={powerOfAttorneys}
        rowData={powerOfAttorneys.map((powerOfAttorney: PowerOfAttorney) => ({
          ...powerOfAttorney,
          borrower_principal: powerOfAttorneyOptions[powerOfAttorney.borrower_principal],
          authorized_agent: powerOfAttorneyOptions[powerOfAttorney.authorized_agent],
          other_authorized_agent_name: powerOfAttorney.other_authorized_agent_name,
        }))}
        openDeleteModal={() => setDeleteModalIsOpen(true)}
      />
      <Modal
        className={`${styles.patchModalBottomPaddingIssue} ${styles.patchModalTooltipZIndex}`}
        passiveModal
        open={formOpen}
        modalHeading={
          editingMode === EditingState.CREATE ? "Add power of attorney" : "Update power of attorney"
        }
        selectorPrimaryFocus="#role"
        onRequestClose={() => {
          updateFormOpen(false);
          updateFormState(initialValues(app.id));
        }}
        onBlur={() => {
          updateFormOpen(false);
          updateFormState(initialValues(app.id));
        }}
      >
        {/* Loading spinner for table */}
        <Loading active={loadingState === LoadingState.ACTIVE} />

        <p>Required fields are marked with an asterisk *</p>
        <AddPowerOfAttorneyForm
          formState={formState}
          powerOfAttorneyOptions={powerOfAttorneyOptions}
          editingMode={editingMode}
          onSubmit={(values: any) => {
            upsertPowerOfAttorney(app.id, values)
              .then(() => {
                refreshApp(app.id);
                updateFormOpen(false);
                updateFormState(initialValues(app.id));
              })
              .catch(err => {
                setErrorMessage(err.meta.error_detail);
                setLoadingState(LoadingState.FINISHED);
              });
          }}
          onRequestClose={() => {
            updateFormOpen(false);
            updateFormState(initialValues(app.id));
          }}
          loading={LoadingState.FINISHED}
        />
      </Modal>
      <Modal
        className={styles.patchModalBottomPaddingIssue}
        danger
        open={deleteModalIsOpen}
        modalHeading="Are you sure you'd like to delete this power of attorney?"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onBlur={() => setDeleteModalIsOpen(false)}
        onRequestClose={() => setDeleteModalIsOpen(false)}
        onRequestSubmit={async () => {
          setLoadingState(LoadingState.ACTIVE);
          deletePowerOfAttorney(app.id, formState.id)
            .then(() => {
              refreshApp(app.id);
            })
            .catch(err => {
              setErrorMessage(err.meta.error_detail);
              setLoadingState(LoadingState.FINISHED);
            });
        }}
      >
        {errorMessage.length > 0 && (
          <InlineNotification
            kind="error"
            notificationType="inline"
            role="alert"
            statusIconDescription="Error"
            subtitle={errorMessage}
            title="Error"
            onCloseButtonClick={() => {
              setErrorMessage("");
            }}
          />
        )}
      </Modal>
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  refreshApp: (appId: number) => loadApp(dispatch, appId),
});

export default connect(null, mapDispatchToProps)(PowerOfAttorneys);
